import { DateFns } from 'lang-utils'
import { CubePresenter, FlipIntent, Logger, Presenter } from 'wdc-cube'
import { AppStorage } from 'src/utils'
import { MainPresenter } from 'src/main/Main.presenter'
import { getTexts, type TCMTexts } from './tcm_texts'
import { TheConsumerMarketKeys } from './tcm_keys'
import { TheConsumerMarketScope, HeaderScope } from './tcm_scopes'
import { TheConsumerMarketService, type SobreOsDados, type EmpresaFilter } from './tcm_service'
import FilterManager from './tcm_filter_manager'

import EmpresasMapPresenter from './tcm_presenter_map'
import EmpresasAbertasFechadasPresenter from './tcm_presenter_empresas_abertas_fechadas'
import EmpresasNoGrupoPresenter from './tcm_presenter_empresas_no_grupo'
import EmpresasFaixaFaturamentoAnualPresenter from './tcm_presenter_empresas_faixa_faturamento_anual'
import EmpresasFaixaFaturamentoConsolidadoGrupoPresenter from './tcm_presenter_empresas_faixa_faturamento_consolidado_grupo'
import EmpresasFaixaFuncionariosPresenter from './tcm_presenter_empresas_faixa_funcionarios'
import EmpresasFaixaFuncionariosGrupoPresenter from './tcm_presenter_empresas_faixa_funcionarios_grupo'
import EmpresasFaixaIdadePresenter from './tcm_presenter_empresas_faixa_idade'
import EmpresaMatrizFiliaisPresenter from './tcm_presenter_empresas_matriz_filiais'
import EmpresasPorSituacaoCadastralPresenter from './tcm_presenter_empresas_situacao_cadastral'
import EmpresasAtivasPorUFPresenter from './tcm_presenter_empresas_ativas_por_uf'
import ForEmpresasTotalPresenter from './tcm_presenter_empresas_total'
import EmpresasTotalAtivasPresenter from './tcm_presenter_empresas_total_ativas'
import EmpresasTotalNovasUltMesPresenter from './tcm_presenter_empresas_total_novas_ultimo_mes'
import EmpresasTableAtivEconomicaPresenter from './tcm_presenter_empresas_table_ativ_economica'
import EmpresasTotalNaturezaJuridicaPresenter from './tcm_presenter_empresas_total_natureza_juridica'

import TcmPresenterListagemTabs from './tcm_presenter_listagem_tabs'
import TcmPresenterForFilterPane from './tcm_presenter_filter_pane'
import TcmPresenterSearchBar from './tcm_presenter_search_bar'

const LOG = Logger.get('TCM-Presenter')

const fns = DateFns.singleton
const appStorage = AppStorage.singleton()
const service = TheConsumerMarketService.singleton()

export type CardType =
    | ''
    | 'totalDeEmpresaAtivas'
    | 'totalDeEmpresaAtivasPorUf'
    | 'totalNovasEmpresasUltimoMes'
    | 'totalDeMatrizesEFiliais'
    | 'totalPorSituacaoCadastral'
    | 'totalPorNaturezaJuridica'
    | 'totalPorAtividadeEconomica'
    | 'relacaoEmpresasAbertasFechadas'
    | 'totalDeEmpresasNoGrupo'
    | 'totalPorFaixaCapitalSocial'
    | 'totalPorFaixaDeFaturamentoAnual'
    | 'totalPorFaixaDeIdade'
    | 'totalPorFaixaDeFuncionarios'
    | 'totalPorFaixaDeFuncionariosGrupo'
    | 'totalPorFaixaDeFaturamentoConsolidadoGrupo'
    | 'listagemDeEmpresas'
    | 'listagemDeQSA'
    | 'listagemDePRF'
    | 'listagemDePES'
    | 'mapa'

export type FetchDataContext = {
    tasks: Promise<void>[]
    source: CardType
    isCard: (name: CardType) => boolean
    filter: EmpresaFilter
}

export class TheConsumerMarketPresenter extends CubePresenter<MainPresenter, TheConsumerMarketScope> {
    // :: Contructor

    constructor(app: MainPresenter) {
        super(app, new TheConsumerMarketScope())
        this.headerBar = new Presenter(this, this.scope.header)
        this.texts = getTexts(app.language)
        this.filterManager = new FilterManager()
        this.filterManager.bindIsAdditiveModeOn(() => !this.scope.keyboard.ctrlKey)

        this.empresasMapa = new EmpresasMapPresenter(this)
        this.empresasAbertasFechadas = new EmpresasAbertasFechadasPresenter(this)
        this.empresasNoGrupo = new EmpresasNoGrupoPresenter(this)
        this.empresasFaixaFaturamentoAnual = new EmpresasFaixaFaturamentoAnualPresenter(this)
        this.empresasFaixaFaturamentoConsolidadoGrupo = new EmpresasFaixaFaturamentoConsolidadoGrupoPresenter(this)
        this.empresasFaixaFuncionarios = new EmpresasFaixaFuncionariosPresenter(this)
        this.empresasFaixaFuncionariosGrupo = new EmpresasFaixaFuncionariosGrupoPresenter(this)
        this.empresasFaixaIdade = new EmpresasFaixaIdadePresenter(this)
        this.empresaMatrizFiliais = new EmpresaMatrizFiliaisPresenter(this)
        this.empresasAtivasPorUF = new EmpresasAtivasPorUFPresenter(this)
        this.empresasPorSituacaoCadastral = new EmpresasPorSituacaoCadastralPresenter(this)
        this.empresasTotal = new ForEmpresasTotalPresenter(this, this.empresasMapa)
        this.empresasTotalAtivas = new EmpresasTotalAtivasPresenter(this)
        this.empresasTotalNovasUltMes = new EmpresasTotalNovasUltMesPresenter(this)
        this.empresasTableAtivEconomica = new EmpresasTableAtivEconomicaPresenter(this)
        this.empresasTotalNaturezaJuridica = new EmpresasTotalNaturezaJuridicaPresenter(this)
        this.listagemTabs = new TcmPresenterListagemTabs(this)

        this.filterPane = new TcmPresenterForFilterPane(this)

        if (appStorage.theConsumerMarketIAEnabled) {
            this.searchBar = new TcmPresenterSearchBar(this)
        }
    }

    // :: State

    readonly texts: TCMTexts
    readonly filterManager: FilterManager

    readonly headerBar: Presenter<HeaderScope, TheConsumerMarketPresenter>
    readonly searchBar?: TcmPresenterSearchBar
    readonly empresasMapa: EmpresasMapPresenter
    readonly empresasAbertasFechadas: EmpresasAbertasFechadasPresenter
    readonly empresasNoGrupo: EmpresasNoGrupoPresenter
    readonly empresasFaixaFaturamentoAnual: EmpresasFaixaFaturamentoAnualPresenter
    readonly empresasFaixaFaturamentoConsolidadoGrupo: EmpresasFaixaFaturamentoConsolidadoGrupoPresenter
    readonly empresasFaixaFuncionarios: EmpresasFaixaFuncionariosPresenter
    readonly empresasFaixaFuncionariosGrupo: EmpresasFaixaFuncionariosGrupoPresenter
    readonly empresasFaixaIdade: EmpresasFaixaIdadePresenter
    readonly empresaMatrizFiliais: EmpresaMatrizFiliaisPresenter
    readonly empresasAtivasPorUF: EmpresasAtivasPorUFPresenter
    readonly empresasPorSituacaoCadastral: EmpresasPorSituacaoCadastralPresenter
    readonly empresasTotal: ForEmpresasTotalPresenter
    readonly empresasTotalAtivas: EmpresasTotalAtivasPresenter
    readonly empresasTotalNovasUltMes: EmpresasTotalNovasUltMesPresenter
    readonly empresasTableAtivEconomica: EmpresasTableAtivEconomicaPresenter
    readonly empresasTotalNaturezaJuridica: EmpresasTotalNaturezaJuridicaPresenter
    readonly listagemTabs: TcmPresenterListagemTabs

    readonly filterPane: TcmPresenterForFilterPane

    private __hasDadosSobreOsDados = false

    // :: Controller lifecycle

    override release() {
        this.app.releaseToolbar(this.headerBar)
        this.listagemTabs.release()
        this.empresasMapa.release()
        this.empresasAbertasFechadas.release()
        this.empresasNoGrupo.release()
        this.empresasFaixaFaturamentoAnual.release()
        this.empresasFaixaFaturamentoConsolidadoGrupo.release()
        this.empresasFaixaFuncionarios.release()
        this.empresasFaixaFuncionariosGrupo.release()
        this.empresasFaixaIdade.release()
        this.empresaMatrizFiliais.release()
        this.empresasAtivasPorUF.release()
        this.empresasPorSituacaoCadastral.release()
        this.empresasTotal.release()
        this.empresasTotalAtivas.release()
        this.empresasTotalNovasUltMes.release()
        this.empresasTableAtivEconomica.release()
        this.empresasTotalNaturezaJuridica.release()
        this.filterPane.release()
        this.searchBar?.release()
        super.release()
        LOG.debug('Finalized')
    }

    override async applyParameters(intent: FlipIntent, initialization: boolean): Promise<boolean> {
        if (!this.app.scope.theConsumerMarketEnabled) {
            await this.app.flipToDefault()
            return false
        }

        const keys = new TheConsumerMarketKeys(this.app, intent)
        keys.tcmPresenter = this

        this.app.scope.toolbar = this.headerBar.scope
        keys.parentSlot(this.scope)

        if (initialization) {
            await this.__initialize(keys)
        } else {
            await this.__update(keys)
        }

        if (keys.last) {
            keys.dialogSlot(null)
        }

        this.searchBar && (await this.searchBar.applyParameters(keys, initialization))
        await this.filterPane.applyParameters(keys, initialization)

        return keys.allow
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    override publishParameters(intent: FlipIntent): void {
        const keys = new TheConsumerMarketKeys(this.app, intent)
        this.listagemTabs.publishParameters(keys)
        //this.searchBar && (this.searchBar.publishParameters(keys))
    }

    // :: Internals

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private async __initialize(keys: TheConsumerMarketKeys) {
        this.searchBar && (await this.searchBar.initialize())
        await this.filterPane.initialize()

        await this.empresasTotal.initialize()
        await this.empresasTotalAtivas.initialize()
        await this.empresasTotalNovasUltMes.initialize()
        await this.empresaMatrizFiliais.initialize()
        await this.empresasAtivasPorUF.initialize()
        await this.empresasPorSituacaoCadastral.initialize()
        await this.empresasTotalNaturezaJuridica.initialize()
        await this.empresasTableAtivEconomica.initialize()
        await this.empresasAbertasFechadas.initialize()
        await this.empresasNoGrupo.initialize()
        await this.empresasFaixaIdade.initialize()
        await this.empresasFaixaFuncionarios.initialize()
        await this.empresasFaixaFuncionariosGrupo.initialize()
        await this.empresasFaixaFaturamentoAnual.initialize()
        await this.empresasFaixaFaturamentoConsolidadoGrupo.initialize()
        await this.empresasMapa.initialize()
        await this.listagemTabs.initialize(keys)

        this.fetchData('').catch(LOG.caught)
    }

    private async __update(keys: TheConsumerMarketKeys) {
        await this.listagemTabs.updateFromIntent(keys)
    }

    getFilter() {
        this.filterPane.publish()
        return this.filterManager.build()
    }

    // prettier-ignore
    async fetchData(source: CardType) {
        const mgr = this.filterManager

        if(!this.__hasDadosSobreOsDados) {
            await service.fetchSobreOsDados().then(this.__updateSobreOsDados.bind(this))
            this.__hasDadosSobreOsDados = true
        }

        this.filterPane.publish()
        this.scope.appliedFilters = this.filterPane.describeAppliedFilters()

        const isCard = (name: CardType) => name !== source
        const tasks: Promise<void>[] = []
        const filter = mgr.build()
        const fetchDataCtx: FetchDataContext = { tasks, source, isCard, filter}

        isCard('totalDeEmpresaAtivas') && tasks.push(this.empresasTotalAtivas.fetch(filter))
        isCard('totalNovasEmpresasUltimoMes') && tasks.push(this.empresasTotalNovasUltMes.fetch(filter))
        isCard('totalDeMatrizesEFiliais') && tasks.push(this.empresaMatrizFiliais.fetch(filter))
        isCard('totalDeEmpresaAtivasPorUf') && tasks.push(this.empresasAtivasPorUF.fetch(filter))
        isCard('totalPorSituacaoCadastral') && tasks.push(this.empresasPorSituacaoCadastral.fetch(filter))
        isCard('totalPorNaturezaJuridica') && tasks.push(this.empresasTotalNaturezaJuridica.fetch(filter))
        isCard('totalPorAtividadeEconomica') && tasks.push(this.empresasTableAtivEconomica.fetch(filter))
        isCard('relacaoEmpresasAbertasFechadas') && tasks.push(this.empresasAbertasFechadas.fetch(filter))
        isCard('totalDeEmpresasNoGrupo') && tasks.push(this.empresasNoGrupo.fetch(filter))
        isCard('totalPorFaixaDeFaturamentoAnual') && tasks.push(this.empresasFaixaFaturamentoAnual.fetch(filter))
        isCard('totalPorFaixaDeIdade') && tasks.push(this.empresasFaixaIdade.fetch(filter))
        isCard('totalPorFaixaDeFuncionarios') && tasks.push(this.empresasFaixaFuncionarios.fetch(filter))
        isCard('totalPorFaixaDeFuncionariosGrupo') && tasks.push(this.empresasFaixaFuncionariosGrupo.fetch(filter))
        isCard('totalPorFaixaDeFaturamentoConsolidadoGrupo') && tasks.push(this.empresasFaixaFaturamentoConsolidadoGrupo.fetch(filter))

        await this.listagemTabs.fetchData(fetchDataCtx)

        tasks.push(this.empresasTotal.fetch(filter))
        tasks.push(this.empresasMapa.fetch(filter))

        return Promise.all(tasks)
    }

    private async __updateSobreOsDados(sobreOsDados: SobreOsDados) {
        const dataDeCorte = fns().parseISO(sobreOsDados.dataCorte)
        // Sem data conhecida a linha é omitida, em vez de mostrar uma data antiga.
        this.scope.dataAtualizacao = sobreOsDados.atualizadoEm
            ? this.app.formatDate(fns().parseISO(sobreOsDados.atualizadoEm))
            : ''
        this.scope.dataDeCorte = this.app.formatDate(dataDeCorte)
        this.filterManager.setDataDeCorte(dataDeCorte)
    }
}
